import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';

import Loading from 'components/Loading';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Card,
  Fade,
  IconButton,
  Modal,
  Typography,
  Backdrop,
} from '@mui/material';
import { BackButton } from 'components/Button';
import dayjs from 'dayjs';
import { CustomerForm } from 'components/Forms';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import ExpensesApplyForm from '../../components/Forms/ExpensesApplyForm';
import * as actions from '../../redux/actions';

export default function EditExpenses({ title, subtitle, propId, onClose }) {
  const params = useParams();
  const id = propId || params.id;
  const dispatch = useDispatch();
  const history = useHistory();
  const expenses = useSelector((state) => state.expenses);
  const projects = useSelector((state) => state.project);
  const budget = useSelector((state) => state.budget);
  const customer = useSelector((state) => state.customer);
  const customerType = useSelector((state) => state.customerType);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm();
  const {
    control: customerControl,
    formState: { errors: customerErrors },
    handleSubmit: handleCustomerSubmit,
    reset: resetCustomer,
    setValue: setCustomerValue,
  } = useForm();

  const [date, setDate] = useState(expenses?.date);

  const [projectSelect, setProjectSelect] = useState('');
  const [budgetSelect, setBudgetSelect] = useState('');
  const [bank, setBank] = useState();
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerSelect, setCustomerSelect] = useState('');

  const eachBudget = _.find(budget?.rows, { _id: budgetSelect?._id });

  useEffect(() => {
    dispatch(actions.expensesGet(id));
    dispatch(actions.projectAll({ lite: true }));
    return () => {};
  }, []);

  useEffect(() => {
    setValue('name', expenses?.name);
    setValue('price', expenses?.price);
    setValue('code', expenses?.code);
    setValue('withholding_tax', expenses?.withholding_tax);
    setValue('withholding_tax_rate', expenses?.withholding_tax_rate || 3);
    setValue('includes_vat', expenses?.includes_vat);
    setValue('vat_amount', expenses?.vat_amount);
    setValue('base_amount', expenses?.base_amount ?? (expenses?.includes_vat ? (expenses?.price - (expenses?.vat_amount || 0)) : (expenses?.net_price ?? expenses?.price)));
    setValue('net_price', expenses?.net_price ?? (expenses?.price - (expenses?.withholding_tax || 0)));
    setValue('customer', expenses?.customer);
    setValue('payee.name', expenses?.payee?.name);
    setValue('payee.account_number', expenses?.payee?.account_number);
    setValue('payee.bank', expenses?.payee?.bank);
    setValue('project', expenses?.project?.id || expenses?.project?._id);
    setValue('expenses_type', expenses?.expenses_type || 'REFUND');
    setDate(expenses?.date);
    setCustomerSelect(expenses?.customer);
    // Initialize project & budget from loaded data
    if (expenses?.project) {
      setProjectSelect(expenses?.project);
    }
    if (expenses?.budget) {
      setBudgetSelect(expenses?.budget);
    }
    return () => {};
  }, [expenses]);

  useEffect(() => {
    dispatch(actions.budgetAll({ project_id: projectSelect?._id || projectSelect?.id }));
    dispatch(actions.customerAll({}));
    dispatch(actions.customerTypeAll({}));
    return () => {};
  }, [projectSelect]);

  const onSubmit = async (data) => {
    const confirm = window.confirm('บันทึกข้อมูล');
    if (confirm) {
      const dataSubmit = {
        ...data,
        bank,
        date: dayjs(date).endOf('day').toISOString(),
        budgetSelect: eachBudget,
        projectSelect,
        note: true,
        applie: expenses?.employee,
        customer: customerSelect,
      };
      try {
        await dispatch(actions.expensesPut(id, dataSubmit));
        if (onClose) {
          onClose();
        } else {
          await dispatch(actions.expensesAll({}));
          history.goBack();
        }
      } catch (error) {
        window.alert(`บันทึกไม่สำเร็จ: ${error?.message || error}`);
      }
    }
  };

  const handleOpenCustomerModal = () => setOpenCustomerModal(true);
  const handleCloseCustomerModal = () => {
    setOpenCustomerModal(false);
    resetCustomer();
  };

  const onSubmitCustomer = async (data) => {
    if (customerSubmitting) return;
    setCustomerSubmitting(true);
    try {
      data.bank = {
        bank_name: bank,
        account_name: data?.bank?.account_name,
        account_number: data?.bank?.account_number,
      };
      await dispatch(actions.customerCreate(data));
      await dispatch(actions.customerAll({}));
      handleCloseCustomerModal();
    } catch (error) {
      // handle error
    } finally {
      setCustomerSubmitting(false);
    }
  };
  const handleAddCustomerFromPayment = () => {
    handleOpenCustomerModal();
  };

  const renderCustomerModal = () => (
    <Modal
      open={openCustomerModal}
      onClose={handleCloseCustomerModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={openCustomerModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">เพิ่มลูกค้าใหม่</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseCustomerModal}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, maxHeight: 'calc(90vh - 64px)', overflowY: 'auto' }}>
            <form onSubmit={handleCustomerSubmit(onSubmitCustomer)}>
              <CustomerForm
                Controller={Controller}
                control={customerControl}
                errors={customerErrors}
                customerType={customerType}
                bank={bank}
                setBank={setBank}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 3,
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseCustomerModal}
                  startIcon={<CloseIcon />}
                  disabled={customerSubmitting}
                >
                  ยกเลิก
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={customerSubmitting}
                  startIcon={customerSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                >
                  {customerSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const renderForm = () => (
    <Card>
      <div className="p-2">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ExpensesApplyForm
            expenses={expenses}
            control={control}
            Controller={Controller}
            date={date}
            setDate={setDate}
            bank={bank}
            setBank={setBank}
            projects={projects}
            setProjectSelect={setProjectSelect}
            projectSelect={projectSelect}
            budgets={budget}
            budgetSelect={budgetSelect}
            setBudgetSelect={setBudgetSelect}
            setValue={setValue}
            customer={customer}
            customerSelect={customerSelect}
            setCustomerSelect={setCustomerSelect}
            watch={watch}
          />
        </form>

      </div>
    </Card>
  );

  if (!expenses?.isLoading && expenses?.isCompleted) {
    return (
      <div>
        {!onClose && <BackButton />}
        {renderForm()}
        {renderCustomerModal()}
      </div>
    );
  }
  return <Loading isLoading />;
}
